// The cards a Neo answer shows on the web (ADR 0266): which a chat keeps, and
// whether an answer places one. The web draws five kinds: maps (TOM-468),
// diagrams (TOM-643, cloud ADR 0386), pictures (TOM-646, cloud ADR 0387),
// camera snapshots (TOM-830, cloud ADR 0411 §7) and pages (TOM-871, cloud ADR
// 0399 §8).
// Every other kind is dropped, as before: a stored answer the app's chat made
// can hold a water or a mode card, and the web shows its words without it. On a
// boat page a card of another boat is dropped, as the charts are.
//
// Pure, so cloud/web's Node vitest covers it.

import {
  neoChatPageBindsResults,
  parseNeoCards,
  type NeoCameraSnapshotCard,
  type NeoCard,
  type NeoChatPageCard,
  type NeoDiagramCard,
  type NeoMapCard,
  type NeoPictureCard,
} from '@neomaris/protocol';

export type { NeoCameraSnapshotCard, NeoChatPageCard, NeoDiagramCard, NeoMapCard, NeoPictureCard };

/** A card the web draws in an answer. */
export type NeoAnswerCard =
  | NeoMapCard
  | NeoDiagramCard
  | NeoPictureCard
  | NeoCameraSnapshotCard
  | NeoChatPageCard;

export type NeoAnswerCardKind = NeoAnswerCard['spec']['kind'];

/** The capability a chat request names to be offered each kind (api_contracts §12.3). */
const CAPABILITY_OF: Readonly<Record<NeoAnswerCardKind, string>> = {
  map: 'maps',
  diagram: 'diagrams',
  picture: 'pictures',
  camera_snapshot: 'camera_snapshots',
  chat_page: 'pages',
};

/** Every kind the chat draws: what it keeps, and asks for where it can. */
export const NEO_ANSWER_CARD_KINDS: readonly NeoAnswerCardKind[] = [
  'map',
  'diagram',
  'picture',
  'camera_snapshot',
  'chat_page',
];

/** Where a chat is mounted, which decides the kinds it is offered. */
export interface NeoAnswerCardSurface {
  /** Inside `BoatShell`: the slide-in chat, the boat's Neo page and home's chat. */
  boatShell: boolean;
  /** It asks for each answer's record (the `answers` capability, TOM-709). */
  answers: boolean;
}

/**
 * True when a chat on `surface` is offered `kind`. A page (cloud ADR 0399 §8)
 * only inside `BoatShell` and with the answer's record: the cloud offers it
 * only with `answers`, and the chat mounted in `AppShell`, on routes that are
 * not a boat's, never claims it. Every other kind everywhere.
 */
export function neoAnswerCardOffered(
  kind: NeoAnswerCardKind,
  surface: NeoAnswerCardSurface
): boolean {
  return kind !== 'chat_page' || (surface.boatShell && surface.answers);
}

/** What the chat asks for, so it is offered every kind it draws there and none it does not. */
export function neoAnswerCardCapabilities(surface: NeoAnswerCardSurface): string[] {
  return NEO_ANSWER_CARD_KINDS.filter((kind) => neoAnswerCardOffered(kind, surface)).map(
    (kind) => CAPABILITY_OF[kind]
  );
}

/**
 * True when the card can be pinned to a board. A picture or a camera snapshot never is, nor a
 * page that binds one of its answer's worked-out figures (cloud ADR 0399 §9): the cloud refuses
 * them (`409 card_not_pinnable`), so they offer no Pin button. A page of readings is pinned.
 */
export function neoAnswerCardPinnable(card: Pick<NeoAnswerCard, 'spec'>): boolean {
  const { spec } = card;
  // Nor is a camera snapshot (TOM-830): it stays in its answer.
  if (spec.kind === 'picture' || spec.kind === 'camera_snapshot') return false;
  return spec.kind !== 'chat_page' || !neoChatPageBindsResults(spec.page);
}

/**
 * The cards of `kinds` in a `cards` record (the stream's event, or a stored
 * answer's field), in order. Any other kind is dropped, and on a boat page a
 * card of another boat.
 */
export function parseNeoAnswerCards(
  raw: unknown,
  boatId?: string,
  kinds: readonly NeoAnswerCardKind[] = NEO_ANSWER_CARD_KINDS
): NeoAnswerCard[] {
  const list = typeof raw === 'object' && raw !== null && 'cards' in raw ? raw.cards : raw;
  return parseNeoCards(list).filter(
    (card: NeoCard): card is NeoAnswerCard =>
      (kinds as readonly string[]).includes(card.spec.kind) &&
      (boatId === undefined || card.spec.boatId === boatId)
  );
}

const CARD_MARKER = /\{\{\s*card\s*:\s*(k[1-9])\s*\}\}/gi;

/**
 * True when the answer places one of its cards, so its bubble takes the log's
 * width and, on the page, the split view. While it streams, any card marker
 * holds a card's place: the records arrive after the text.
 */
export function answerShowsCard(
  content: string,
  cards: readonly { id: string }[] | undefined,
  streaming: boolean
): boolean {
  for (const match of content.matchAll(CARD_MARKER)) {
    const id = match[1]!.toLowerCase();
    if (streaming || cards?.some((c) => c.id === id)) return true;
  }
  return false;
}
